import { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import RefillCardEntry from './RefillCardEntry';
import api from '../../services/api';
import * as orderService from '../../services/orderService';
import styles from './RefillCheckoutModal.module.css';
import { getSupplyLabel, toPerMonthRate } from '../../utils/supplyTerms';
import { formatAppointmentDisplay, toRefillAppointmentPayload } from '../../utils/refillAppointment';
import { deriveHostedCardKey } from '../../utils/checkoutPaymentArm';
import { customerDosageText } from '../../utils/doctorNowDosageSummary';

const DURATIONS = [1, 3, 6];

const formatCents = (cents) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const validateCard = ({ cardNumber, expirationDate, cvv }) => {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 13 || digits.length > 16) return 'Please enter a valid card number';
  if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(expirationDate)) return 'Please enter a valid expiry (MM/YY)';
  if (!/^\d{3,4}$/.test(cvv)) return 'Please enter a valid CVV';
  return null;
};

/**
 * `appointment` (card 6826) is the slot chosen on the shared purchase calendar, in that page's
 * DISPLAY shape: { date, time, timezone, utcTime }. It is null for every non-DrCare refill, which
 * is what keeps the DoctorNow/unknown request body byte-identical to today's.
 */
const RefillCheckoutModal = ({ isOpen, order, appointment = null, onClose, onScheduleAppointment }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [refill, setRefill] = useState(null);
  const [duration, setDuration] = useState(3);
  const [useCardOnFile, setUseCardOnFile] = useState(false);
  const [card, setCard] = useState({ cardNumber: '', expirationDate: '', cvv: '' });
  const [formError, setFormError] = useState(null);
  const [declined, setDeclined] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState(null);
  // Card #54400 (D4). Which surface may collect a NEW card — asked of the same config endpoint and
  // the same derivation checkout routes on, so the modal and the server's raw-card refusal agree.
  // Fails closed to the raw fields: an absent config is the incumbent arm.
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [mintedMethodId, setMintedMethodId] = useState(null);
  const hostedKey = deriveHostedCardKey(paymentConfig);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    api.get('/users/me/payment-method/config')
      .then((config) => { if (!cancelled) setPaymentConfig(config); })
      .catch(() => { if (!cancelled) setPaymentConfig(null); });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !order) return;
    setLoading(true);
    setLoadError(null);
    setRefill(null);
    setDuration(3);
    setCard({ cardNumber: '', expirationDate: '', cvv: '' });
    setFormError(null);
    setDeclined(null);
    setSuccess(false);
    setBookedAppointment(null);
    setMintedMethodId(null);
    orderService.calculateRefill(order.id)
      .then((response) => {
        setRefill(response.refill);
        setUseCardOnFile(Boolean(response.refill?.hasCardOnFile));
      })
      .catch((error) => setLoadError(error.message || 'Unable to load refill pricing'))
      .finally(() => setLoading(false));
  }, [isOpen, order]);

  const pricing = refill?.pricing?.[duration];
  const totalCents = pricing?.totalCents;

  // Read the server's decision; ⛔ never re-derive one from the product name or provider string.
  // `schedulingRequired` is true for confirmed-DrCare orders ONLY — `unknown` is deliberately
  // false there, so an indeterminate order keeps its existing direct flow.
  const schedulingRequired = Boolean(refill?.schedulingRequired);
  const appointmentDisplay = formatAppointmentDisplay(appointment);

  // Reachable: Orders routes DrCare refills to the calendar first, but if that pre-flight
  // `calculateRefill` probe fails (network), it falls through and opens this modal directly.
  // Without this guard, Pay is enabled on an order the server will reject 400 for want of a slot.
  const missingRequiredAppointment = schedulingRequired && !appointment?.utcTime;

  const handleSubmit = async () => {
    setFormError(null);
    setDeclined(null);
    if (missingRequiredAppointment) {
      setFormError('Please choose an appointment time before paying for this refill.');
      return;
    }
    if (!useCardOnFile && hostedKey && !mintedMethodId) {
      setFormError('Please enter and confirm your card details first.');
      return;
    }
    // Card #54400 (D4). On the hosted arm the body carries the provider's minted reference and
    // nothing else, so a card number cannot reach the request even if one was typed earlier.
    const payment = useCardOnFile
      ? { useCardOnFile: true }
      : hostedKey
        ? { paymentMethodId: mintedMethodId }
        : {
            cardNumber: card.cardNumber.replace(/\D/g, ''),
            expirationDate: card.expirationDate,
            cvv: card.cvv
          };
    if (!useCardOnFile && !hostedKey) {
      const validationError = validateCard({ ...payment, expirationDate: card.expirationDate });
      if (validationError) {
        setFormError(validationError);
        return;
      }
    }
    setSubmitting(true);
    try {
      // Spread-only-when-present: a non-DrCare refill sends exactly the body it sent before,
      // with no `appointment` key at all.
      const appointmentPayload = toRefillAppointmentPayload(appointment);
      const response = await orderService.submitRefill(order.id, {
        durationMonths: duration,
        payment,
        ...(appointmentPayload ? { appointment: appointmentPayload } : {})
      });
      setBookedAppointment(response?.appointment ?? null);
      setSuccess(true);
    } catch (error) {
      setMintedMethodId(null);  // a declined card is re-entered, never silently retried
      if (error.status === 402) {
        const message = error.message && error.message !== 'Payment declined'
          ? error.message
          : 'Your payment could not be processed.';
        setDeclined(message);
      } else {
        setFormError(error.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loading) return <p className={styles.stateText}>Loading refill pricing...</p>;

    if (loadError) {
      return (
        <div className={styles.content}>
          <div className={styles.errorBox}>{loadError}</div>
          <div className={styles.actions}>
            <button className={styles.secondaryBtn} onClick={onClose}>Close</button>
          </div>
        </div>
      );
    }

    if (success) {
      return (
        <div className={styles.content}>
          <div className={styles.successBox}>
            <span className={styles.successTitle}>Your refill request has been received</span>
            <p className={styles.successText}>
              Your payment of {formatCents(totalCents)} was successful. Our team will process
              your {duration}-month refill of {refill.product?.name} and follow up by email.
            </p>
            {/* The server reports `booked` honestly — it stays 200 with booked:false when the
                DrCare call fails, precisely so the client does not claim a confirmation that
                never happened. Both branches below are true statements. */}
            {bookedAppointment?.formatted && (
              <p className={styles.successText}>
                {bookedAppointment.booked
                  ? `Your telehealth appointment is confirmed for ${bookedAppointment.formatted}`
                  : `We have recorded your requested telehealth time of ${bookedAppointment.formatted} and will confirm it by email`}
                {bookedAppointment.timezone ? ` (${bookedAppointment.timezone}).` : '.'}
              </p>
            )}
          </div>
          <div className={styles.actions}>
            <button className={styles.primaryBtn} onClick={onClose}>Close</button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.content}>
        <div className={styles.orderContext}>
          <span className={styles.productName}>{refill.product?.name}</span>
          {/* Card 54406: the server's provider decides; a Doctor Now refill shows the clinician
              label, never the internal default as the customer's dose. */}
          {refill.originalDosageLabel && (
            <span className={styles.dosage}>
              {customerDosageText({ isDoctorNow: refill.provider === 'doctornow', amount: refill.originalDosageLabel })}
            </span>
          )}
          <span className={styles.orderMeta}>
            Original order {refill.orderNumber}
            {refill.orderDate && ` — ${new Date(refill.orderDate).toLocaleDateString()}`}
          </span>
        </div>

        <div className={styles.section}>
          <span className={styles.sectionLabel}>Supply Duration</span>
          <div className={styles.durationGrid}>
            {DURATIONS.map((months) => {
              const option = refill.pricing?.[months];
              if (!option?.available) return null;
              return (
                <button
                  key={months}
                  type="button"
                  className={`${styles.durationCard} ${duration === months ? styles.durationSelected : ''}`}
                  onClick={() => setDuration(months)}
                >
                  <span className={styles.durationLabel}>{getSupplyLabel(months)}</span>
                  <span className={styles.durationPrice}>{formatCents(toPerMonthRate(option.monthlyPrice, months))}/mo</span>
                  <span className={styles.durationTotal}>{formatCents(option.totalCents)} total</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Card 40756 — gated on the SERVER's `schedulingRequired`, ⛔ not on whether an
            appointment happens to be in hand. An async DrCare refill can still arrive carrying a
            stale `refillAppointment` in route state (Orders keeps the slot across a Back, and
            `handleScheduleAppointment` re-enters the calendar with it), and the old
            `appointmentDisplay ||` arm then rendered a "Telehealth Appointment" section — and a
            "Change time" button — on a request that needs no visit at all. Gating on the flag makes
            the whole section structurally absent whenever scheduling is not required. */}
        {schedulingRequired && (
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Telehealth Appointment</span>
            {appointmentDisplay ? (
              <div className={styles.appointmentBox}>
                <span className={styles.appointmentTime}>{appointmentDisplay}</span>
                {appointment?.timezone && (
                  <span className={styles.appointmentTz}>{appointment.timezone}</span>
                )}
                {onScheduleAppointment && (
                  <button
                    type="button"
                    className={styles.appointmentChangeBtn}
                    onClick={onScheduleAppointment}
                    disabled={submitting}
                  >
                    Change time
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.errorBox}>
                <span className={styles.errorTitle}>An appointment time is required</span>
                <p className={styles.errorText}>
                  This refill includes a telehealth visit, so a time must be chosen before payment.
                </p>
                {onScheduleAppointment && (
                  <button
                    type="button"
                    className={styles.appointmentChangeBtn}
                    onClick={onScheduleAppointment}
                  >
                    Choose a time
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className={styles.section}>
          <span className={styles.sectionLabel}>Payment</span>
          {refill.hasCardOnFile && (
            <div className={styles.paymentToggle}>
              <button
                type="button"
                className={`${styles.toggleBtn} ${useCardOnFile ? styles.toggleActive : ''}`}
                onClick={() => setUseCardOnFile(true)}
              >
                Use card on file{refill.cardLast4 ? ` (•••• ${refill.cardLast4})` : ''}
              </button>
              <button
                type="button"
                className={`${styles.toggleBtn} ${!useCardOnFile ? styles.toggleActive : ''}`}
                onClick={() => setUseCardOnFile(false)}
              >
                Use a different card
              </button>
            </div>
          )}
          {!useCardOnFile && (
            <RefillCardEntry
              hostedKey={hostedKey}
              card={card}
              onCardChange={setCard}
              mintedMethodId={mintedMethodId}
              onMinted={setMintedMethodId}
            />
          )}
        </div>

        {declined && (
          <div className={styles.errorBox}>
            <span className={styles.errorTitle}>Payment declined</span>
            <p className={styles.errorText}>
              {declined} Please check your card details and try again.
            </p>
          </div>
        )}
        {formError && <div className={styles.errorBox}>{formError}</div>}

        <div className={styles.totalRow}>
          <span>Total due today</span>
          <span className={styles.totalAmount}>
            {totalCents != null ? formatCents(totalCents) : '—'}
          </span>
        </div>

        <div className={styles.actions}>
          <button className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
            Close
          </button>
          <button
            className={styles.primaryBtn}
            onClick={handleSubmit}
            disabled={submitting || totalCents == null || missingRequiredAppointment}
          >
            {submitting
              ? 'Processing...'
              : declined
                ? 'Retry Payment'
                : `Pay ${totalCents != null ? formatCents(totalCents) : ''}`}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Refill — Same Dosage">
      {renderBody()}
    </Modal>
  );
};

export default RefillCheckoutModal;
